import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { Input, Select } from '../../common';
import { UserRole } from '../../auth/types/auth.types';
import {
  CreateUserRequest,
  UpdateUserRequest,
  User,
  roleOptions,
} from '../types/users.types';
import { useBranchOffices, useSellers } from '../../branch-office';

interface UserFormProps {
  user?: User | null;
  onSubmit: (data: CreateUserRequest | UpdateUserRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

interface PasswordValidation {
  hasMinLength: boolean;
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
}

const chainOptions = [
  { label: 'Marcha', value: 'MRH' },
  { label: 'Flexi', value: 'TAF' },
  { label: 'Trender', value: 'TRE' },
  { label: 'Vazza', value: 'VAZ' },
];

const UserForm: React.FC<UserFormProps> = ({
  user,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<CreateUserRequest>({
    email: '',
    password: '',
    branchOffice: undefined,
    branchOfficeName: undefined,
    userCode: '',
    employeeNumber: undefined,
    name: '',
    role: '' as unknown as UserRole, // sin seleccion: obliga a elegir el rol a proposito
    // `undefined` = "que decida el rol". La API pone `false` a las cuentas de
    // dispositivo (tienda, bodega, vendedor, consulta) y `true` al resto. Ver
    // ROLES_DE_DISPOSITIVO en create-user.use-case.ts.
    requiresPasswordChange: undefined,
  });

  const [passwordValidation, setPasswordValidation] =
    useState<PasswordValidation>({
      hasMinLength: false,
      hasUpperCase: false,
      hasLowerCase: false,
      hasNumber: false,
      hasSpecialChar: false,
    });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const {
    branchOffices,
    fetchBranchOfficesByChain,
    isLoading: isBranchOfficesLoading,
    clearBranchOffices,
  } = useBranchOffices();
  const {
    sellers,
    fetchSellersByChainAndBranch,
    isLoading: isSellersLoading,
    clearSellers,
  } = useSellers();

  const isEditMode = !!user;

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        password: '',
        branchOffice: user.branchOffice,
        branchOfficeName: user.branchOfficeName,
        userCode: user.userCode,
        role: user.role,
        externalUserId: user.externalUserId,
        chainId: user.chainId,
        regionId: user.regionId,
        employeeNumber: user.employeeNumber,
        name: (user as any).name || '',
        // ⚠️ NO poner `true` aqui. El listado de usuarios no devuelve la bandera,
        // asi que el formulario no sabe su valor real; ponerla a `true` y
        // enviarla en cada edicion BLOQUEABA la cuenta al guardar cualquier
        // cambio (un nombre, un correo). Con la API vieja pasaba inadvertido
        // porque `update-user` descartaba el campo en silencio; desde el
        // 2026-08-25 lo persiste. `undefined` = "no tocar".
        requiresPasswordChange: undefined,
      });
    }
  }, [user]);

  // Cargar sucursales cuando cambia chainId o cuando se carga el usuario en modo edición
  useEffect(() => {
    if (formData.chainId) {
      fetchBranchOfficesByChain(formData.chainId);
    } else {
      clearBranchOffices();
    }
  }, [formData.chainId, fetchBranchOfficesByChain, clearBranchOffices]);

  // Cargar sellers cuando se selecciona una sucursal
  useEffect(() => {
    if (formData.chainId && formData.branchOffice) {
      fetchSellersByChainAndBranch(formData.chainId, formData.branchOffice);
    } else {
      clearSellers();
    }
  }, [
    formData.chainId,
    formData.branchOffice,
    fetchSellersByChainAndBranch,
    clearSellers,
  ]);

  const validatePassword = (password: string): PasswordValidation => {
    return {
      hasMinLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
  };

  const handlePasswordChange = (value: string) => {
    setFormData((prev) => ({ ...prev, password: value }));
    setPasswordValidation(validatePassword(value));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.name?.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (!formData.role) {
      newErrors.role = 'Selecciona un rol';
    }

    if (!isEditMode && !formData.password) {
      newErrors.password = 'Password is required';
    } else if (
      formData.password &&
      !Object.values(passwordValidation).every(Boolean)
    ) {
      newErrors.password = 'Password does not meet security requirements';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (isEditMode) {
        const updateData: UpdateUserRequest = {
          email: formData.email,
          branchOffice: formData.branchOffice,
          branchOfficeName: formData.branchOfficeName,
          userCode: formData.userCode,
          role: formData.role,
          externalUserId: formData.externalUserId,
          chainId: formData.chainId,
          regionId: formData.regionId,
          employeeNumber: formData.employeeNumber,
          name: formData.name,
        };

        // Solo viaja si el operador eligio explicitamente. Si no, se omite y la
        // API deja la bandera como estaba.
        if (formData.requiresPasswordChange !== undefined) {
          updateData.requiresPasswordChange = formData.requiresPasswordChange;
        }

        if (formData.password) {
          updateData.password = formData.password;
        }

        await onSubmit(updateData);
      } else {
        await onSubmit(formData);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const isPasswordValid = Object.values(passwordValidation).every(Boolean);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-xl font-semibold mb-6 text-gray-800">
        {isEditMode ? 'Editar usuario' : 'Crear usuario'}
      </div>

      {/* Role */}
      <Select
        id="role"
        label="Rol"
        value={formData.role}
        options={roleOptions}
        onChange={(value) => setFormData((prev) => ({ ...prev, role: value }))}
        placeholder="— Selecciona un rol —"
        error={errors.role}
        required
      />

      {/* Email */}
      <Input
        id="email"
        label="Email"
        type="email"
        value={formData.email}
        onChange={(value) => setFormData((prev) => ({ ...prev, email: value }))}
        placeholder="Ingrese el email"
        error={errors.email}
        required
      />

      {/* Password */}
      <div className="field">
        <Input
          id="password"
          label={`Contraseña ${!isEditMode ? '*' : ''}`}
          type="password"
          value={formData.password}
          onChange={handlePasswordChange}
          placeholder={
            isEditMode
              ? 'Dejar vacío para mantener la contraseña actual'
              : 'Ingrese la contraseña'
          }
          error={errors.password}
          showPasswordToggle
        />

        {/* Password validation messages */}
        {formData.password && (
          <div className="mt-3 space-y-2 bg-gray-50 p-3 rounded-lg">
            <div
              className={`text-sm font-medium ${
                passwordValidation.hasMinLength
                  ? 'text-green-600'
                  : 'text-red-600'
              }`}
            >
              {passwordValidation.hasMinLength ? '✓' : '✗'} Al menos 8
              caracteres
            </div>
            <div
              className={`text-sm font-medium ${
                passwordValidation.hasUpperCase
                  ? 'text-green-600'
                  : 'text-red-600'
              }`}
            >
              {passwordValidation.hasUpperCase ? '✓' : '✗'} Al menos una letra
              mayúscula
            </div>
            <div
              className={`text-sm font-medium ${
                passwordValidation.hasLowerCase
                  ? 'text-green-600'
                  : 'text-red-600'
              }`}
            >
              {passwordValidation.hasLowerCase ? '✓' : '✗'} Al menos una letra
              minúscula
            </div>
            <div
              className={`text-sm font-medium ${
                passwordValidation.hasNumber ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {passwordValidation.hasNumber ? '✓' : '✗'} Al menos un número
            </div>
            <div
              className={`text-sm font-medium ${
                passwordValidation.hasSpecialChar
                  ? 'text-green-600'
                  : 'text-red-600'
              }`}
            >
              {passwordValidation.hasSpecialChar ? '✓' : '✗'} Al menos un
              carácter especial
            </div>
          </div>
        )}
      </div>

      {/* chain ID */}
      <Select
        id="chainId"
        label="ID de cadena"
        value={formData.chainId}
        options={chainOptions}
        onChange={(value) => {
          setFormData((prev) => ({
            ...prev,
            chainId: value,
            branchOffice: undefined,
          }));
        }}
        placeholder="Seleccione el ID de cadena (opcional)"
        showClear
      />

      {/* Branch Office */}
      <Select
        id="branchOffice"
        label="Sucursal / Tienda (CATCIA)"
        value={formData.branchOffice}
        options={branchOffices.map((branch) => ({
          label: `${branch.name} (ID: ${branch.id})`,
          value: branch.id,
        }))}
        onChange={(value) => {
          const selectedBranch = branchOffices.find(
            (branch) => branch.id === Number(value)
          );
          setFormData((prev) => ({
            ...prev,
            branchOffice: value ? Number(value) : undefined,
            branchOfficeName: selectedBranch?.name || undefined,
            regionId: selectedBranch?.region || undefined,
            externalUserId: undefined, // Limpiar externalUserId al cambiar branchOffice
            userCode: '', // Limpiar userCode al cambiar branchOffice
            name:
              prev.role === UserRole.BRANCH_OFFICE
                ? selectedBranch?.name || prev.name
                : prev.name, // Limpiar name al cambiar branchOffice
          }));
        }}
        placeholder={
          isBranchOfficesLoading
            ? 'Cargando sucursales...'
            : formData.chainId
            ? 'Seleccione la sucursal'
            : 'Primero seleccione el ID de cadena'
        }
        disabled={!formData.chainId || isBranchOfficesLoading}
        showClear
      />

      {/* External User ID (Seller) */}
      <Select
        id="externalUserId"
        label="Usuario (opcional)"
        value={formData.externalUserId}
        options={sellers.map((seller) => ({
          label: `${seller.name} - (${seller.externalUserId})`,
          value: seller.externalUserId,
        }))}
        onChange={(value) => {
          if (value) {
            const selectedSeller = sellers.find(
              (seller) => seller.externalUserId === value
            );
            setFormData((prev) => ({
              ...prev,
              externalUserId: value,
              name: selectedSeller?.name || prev.name,
              userCode: selectedSeller?.userCode || prev.userCode,
            }));
          } else {
            setFormData((prev) => ({
              ...prev,
              externalUserId: undefined,
              userCode: '',
            }));
          }
        }}
        placeholder={
          isSellersLoading
            ? 'Cargando usuarios...'
            : formData.branchOffice
            ? 'Seleccione el usuario'
            : 'Primero seleccione una sucursal'
        }
        disabled={!formData.branchOffice || isSellersLoading}
        showClear
      />

      {/* Name */}
      <Input
        id="name"
        label="Nombre"
        value={formData.name || ''}
        onChange={(value) => setFormData((prev) => ({ ...prev, name: value }))}
        placeholder="Ingrese el nombre"
        error={errors.name}
        required
      />

      {/* User Code */}
      <Input
        id="userCode"
        label="Código de usuario"
        value={formData.userCode || ''}
        onChange={(value) =>
          setFormData((prev) => ({ ...prev, userCode: value }))
        }
        placeholder="Código de usuario"
        error={errors.userCode}
        readOnly
      />

      {/* Region ID */}
      <Input
        id="regionId"
        label="ID de región"
        value={formData.regionId || ''}
        onChange={(value) =>
          setFormData((prev) => ({ ...prev, regionId: value }))
        }
        placeholder="ID de región"
        readOnly
      />

      {/* Requires Password Change */}
      <div className="field">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          ¿Solicitar cambio de contraseña?
        </label>
        <div className="flex flex-col space-y-3">
          <div className="flex items-center">
            <RadioButton
              inputId="requiresPasswordChange_default"
              name="requiresPasswordChange"
              onChange={() =>
                setFormData((prev) => ({
                  ...prev,
                  requiresPasswordChange: undefined,
                }))
              }
              checked={formData.requiresPasswordChange === undefined}
            />
            <label
              htmlFor="requiresPasswordChange_default"
              className="ml-2 text-sm text-gray-700"
            >
              {isEditMode
                ? 'Sin cambio (dejar la cuenta como está)'
                : 'Según el rol: las cuentas de tienda entran directo; las demás cambian contraseña'}
            </label>
          </div>
          <div className="flex items-center">
            <RadioButton
              inputId="requiresPasswordChange_yes"
              name="requiresPasswordChange"
              value={true}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  requiresPasswordChange: e.value,
                }))
              }
              checked={formData.requiresPasswordChange === true}
            />
            <label
              htmlFor="requiresPasswordChange_yes"
              className="ml-2 text-sm text-gray-700"
            >
              Sí, solicitar cambio de contraseña en el primer inicio de sesión
            </label>
          </div>
          <div className="flex items-center">
            <RadioButton
              inputId="requiresPasswordChange_no"
              name="requiresPasswordChange"
              value={false}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  requiresPasswordChange: e.value,
                }))
              }
              checked={formData.requiresPasswordChange === false}
            />
            <label
              htmlFor="requiresPasswordChange_no"
              className="ml-2 text-sm text-gray-700"
            >
              No, permitir usar la contraseña actual
            </label>
          </div>
        </div>
        <small className="text-gray-500 mt-2 block">
          {isEditMode
            ? 'Elegir «Sí» o «No» sobrescribe la bandera de la cuenta. «Sin cambio» la deja como está.'
            : 'Las cuentas de tienda son compartidas por dispositivo: pedirles cambio de contraseña las deja sin poder operar.'}
        </small>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <Button
          type="button"
          label="Cancelar"
          className="p-button-secondary"
          onClick={onCancel}
          disabled={isLoading}
        />
        <Button
          type="submit"
          label={isEditMode ? 'Actualizar usuario' : 'Crear usuario'}
          className="p-button-primary"
          loading={isLoading}
          disabled={!isEditMode && !isPasswordValid}
        />
      </div>
    </form>
  );
};

export default UserForm;
